// A row of the one schedules store in words (pure; scheduleFormModel.selftest.ts
// runs these too): when, its state, how the last run went, why it is held,
// where it came from, and the list grouped by the agent whose chat it
// reports in.

import type { UnifiedSchedule } from "@/api/schedulesUnified";
import type { StatusTone } from "@/components/ui/StatusBadge";
import { isTaskStatus, taskStatusWord } from "@/features/tasks/taskModel";
import { browserZone, zoneLabel } from "@/features/workflows/editor/scheduleModel";
import { formatDateTime, pluralize, type DateInput } from "@/lib/format";

import { cronListWords, lastProblem, type LastProblem } from "../scheduleOverviewModel";
import { afterOf, afterWords } from "./chainModel";

export interface RowWord {
  label: string;
  tone: StatusTone;
}

/** "Every 2 hours", "Every 90 minutes", "Every day". */
export function everyWords(minutes: number): string {
  if (minutes % 1440 === 0) return minutes === 1440 ? "Every day" : `Every ${pluralize(minutes / 1440, "day")}`;
  if (minutes % 60 === 0) return minutes === 60 ? "Every hour" : `Every ${pluralize(minutes / 60, "hour")}`;
  return `Every ${pluralize(minutes, "minute")}`;
}

/** When it runs: "Weekdays at 08:00 (New York)", "Every 2 hours", "Once, Tomorrow 09:00", "After Morning analysis succeeds"; "Custom times", never a raw cron. */
export function whenText(row: Pick<UnifiedSchedule, "when">, now?: DateInput, browser = browserZone()): string {
  const w = row.when;
  const after = afterOf(row);
  if (after) return afterWords(after);
  if (w.cron) return `${cronListWords(w.cron)}${w.tz && browser && w.tz !== browser ? ` (${zoneLabel(w.tz)})` : ""}`;
  if (w.every_minutes) return everyWords(w.every_minutes);
  return w.at ? `Once, ${formatDateTime(w.at, { inline: true }, now)}` : "Once";
}

/** Its state as a badge (null: it runs on its timetable). */
export function stateWord(row: Pick<UnifiedSchedule, "state">): RowWord | null {
  switch (row.state) {
    case "paused":
      return { label: "Paused", tone: "warning" };
    case "ended":
      return { label: "Ended", tone: "neutral" };
    case "held":
      return { label: "Held", tone: "warning" };
    case "awaiting_approval":
      return { label: "Waiting for your OK", tone: "warning" };
    default:
      return null;
  }
}

const NOTE_WORD: Readonly<Record<string, RowWord>> = {
  sent: { label: "Sent", tone: "success" },
  delivered: { label: "Sent", tone: "success" },
  sending: { label: "Sending", tone: "info" },
  pending: { label: "Sending", tone: "info" },
  failed: { label: "Not delivered", tone: "danger" },
  uncertain: { label: "May not have arrived", tone: "warning" },
};

type SlotRow = Pick<UnifiedSchedule, "last_error"> & Partial<Pick<UnifiedSchedule, "when" | "last_run_at" | "next_run_at">>;

/** A slot that did not start, in the words both Schedules pages use (../scheduleOverviewModel.lastProblem). */
export function rowProblem(row: SlotRow): LastProblem | null {
  const w = row.when;
  return lastProblem({ last_error: row.last_error, last_run_at: row.last_run_at, next_run_at: row.next_run_at, cron: w?.cron, every_minutes: w?.every_minutes, at: w?.at });
}

/** How the last run went (null: none yet); a slot that did not start says so first. */
export function lastWord(row: Pick<UnifiedSchedule, "who" | "last_status" | "last_error"> & Partial<Pick<UnifiedSchedule, "when" | "last_run_at" | "next_run_at">>): RowWord | null {
  if (row.last_error) return rowProblem(row)?.badge ?? null;
  const s = (row.last_status ?? "").trim();
  if (!s) return null;
  if (row.who.kind === "message") return NOTE_WORD[s] ?? { label: s, tone: "neutral" };
  if (isTaskStatus(s)) {
    const w = taskStatusWord(s);
    return { label: w.label, tone: w.tone };
  }
  return { label: s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " "), tone: "neutral" };
}

const HELD: Readonly<Record<string, string>> = {
  agent_removed: "Its agent is gone: change it or delete it.",
  workflow_deleted: "Its workflow is in the trash: restore the workflow to bring this back, or delete it.",
};

/** Why a held row does not run, in words ("" for one that is not held). */
export function heldText(row: Pick<UnifiedSchedule, "held">): string {
  if (!row.held) return "";
  return HELD[row.held] ?? "Held: change it or delete it.";
}

const ORIGIN: Readonly<Record<string, string>> = {
  checkin: "Check-in", subagent: "Runs a subagent", workflow_editor: "Workflow timetable",
  family: "Family reminder", import: "Moved from the old schedules",
};

/**
 * A small tag for where a row came from ("" for one made here or by an
 * agent in chat). A scheduled take an old person project set up (named
 * `persona-<slug>-<hash>--<what>`) was made by an agent in chat too, so it
 * carries no tag since 25 Oct 2026; its name still reads "Ray Dalio: …".
 */
export function originText(row: Pick<UnifiedSchedule, "origin" | "name">): string {
  return ORIGIN[row.origin] ?? "";
}

/** What an operator may do to it here: a managed row (a check-in) is paused or resumed only. */
export function canEdit(row: Pick<UnifiedSchedule, "managed" | "state">): boolean {
  return !row.managed;
}

export interface OwnerGroup<T> {
  /** The owner's template; "" = rows reporting through no agent (a team, a workflow, a quiet row). */
  owner: string;
  rows: T[];
}

/** The rows by owner, agents in `order` first (the directory's), then any other owner, then none. */
export function groupByOwner<T extends Pick<UnifiedSchedule, "owner" | "name">>(rows: readonly T[], order: readonly string[]): OwnerGroup<T>[] {
  const by = new Map<string, T[]>();
  for (const r of rows) {
    const list = by.get(r.owner) ?? [];
    list.push(r);
    by.set(r.owner, list);
  }
  const owners = [...order.filter((o) => by.has(o)), ...[...by.keys()].filter((o) => o && !order.includes(o)).sort()];
  if (by.has("")) owners.push("");
  return owners.map((owner) => ({ owner, rows: [...(by.get(owner) ?? [])].sort((a, b) => a.name.localeCompare(b.name)) }));
}
